import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { defaultApiConfig, buildUrl, buildHeaders, createApiError } from '@callbridge/ui';
import { API_BASE } from '../lib/apiBase';
import { SpadeSvg, HeartSvg, DiamondSvg, ClubSvg } from '../components/SuitIcons';
import { trackEvent } from '../lib/analytics';

export function RegisterPage() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { startLoading, loginSuccess, authError, isLoading, error } = useAuthStore();
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      authError('Passwords do not match');
      return;
    }

    startLoading();

    try {
      const path = '/auth/register';
      const body = { username, email, password };
      const res = await fetch(buildUrl(API_BASE, path), {
        method: 'POST',
        headers: buildHeaders(defaultApiConfig, { body, skipAuth: true }),
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = (await res.json()) as { error?: { code: string; message: string } };
        throw createApiError(res.status, { status: 'error', error: data.error });
      }

      const json = (await res.json()) as {
        data: {
          user: {
            id: string;
            username: string;
            email: string;
            gamesPlayed: number;
            gamesWon: number;
            rating: number;
            createdAt: string;
          };
          tokens: { accessToken: string; refreshToken: string };
        };
      };

      loginSuccess(json.data.user, json.data.tokens.accessToken, json.data.tokens.refreshToken);
      trackEvent('sign_up', { method: 'email' });
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Registration failed';
      authError(msg);
    }
  }

  return (
    <div className="page auth-page">
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      <div className="auth-brand">
        <SpadeSvg className="brand-icon" />
        <span className="brand-text">Call Bridge</span>
      </div>

      <div className="auth-card">
        <h1>Register</h1>
        <p className="auth-subtitle">Create your Call Bridge account</p>

        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={20}
              autoComplete="username"
              disabled={isLoading}
            />
          </div>

          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <div className="form-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              disabled={isLoading}
            />
          </div>

          <div className="form-field">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              disabled={isLoading}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Creating account...' : 'Register'}
          </button>
        </form>

        <p className="auth-switch">
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
}
